/**
 * ProBookingRequestSheet
 *
 * Pros only: every pending (non-rideshare) booking request addressed to
 * this pro shows in a persistent slide-up with its 30-minute expiry ring.
 * The sheet only closes when the pro accepts or declines — or the request
 * expires / is cancelled by the customer.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Briefcase, Calendar, Check, Loader2, MapPin, Paperclip, User, X } from "lucide-react";
import { API, useAuth } from "@/App";
import BookingCountdown from "@/components/BookingCountdown";
import PersistentSheet from "@/components/requests/PersistentSheet";
import VoiceNotePlayer from "@/components/VoiceNotePlayer";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { notifyBookingUpdated } from "@/lib/requestWindow";

const POLL_MS = 8000;

const isLive = (b) => {
  const t = Date.parse(b?.expires_at || "");
  return !Number.isFinite(t) || t > Date.now();
};

const whenLabel = (b) => {
  const iso = b.scheduled_date || b.preferred_date;
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_) { return null; }
};

export default function ProBookingRequestSheet() {
  const { token, user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [gone, setGone] = useState(null);
  const [busy, setBusy] = useState(false);
  const seenRef = useRef(new Set());

  const booking = queue[0] || null;
  const bookingId = booking?.id;

  const poll = useCallback(async () => {
    if (!token || !user?.id) return;
    try {
      const { data } = await axios.get(`${API}/bookings?token=${token}&status=pending`);
      const live = (data || []).filter((b) => b.booking_type !== "rideshare" && b.status === "pending" && b.artisan_user_id === user.id && isLive(b));
      setQueue((prev) => {
        const byId = new Map(live.map((b) => [b.id, b]));
        const kept = prev.filter((b, i) => i === 0 || byId.has(b.id)).map((b) => byId.get(b.id) || b);
        const ids = new Set(kept.map((b) => b.id));
        const fresh = live.filter((b) => !ids.has(b.id) && !seenRef.current.has(b.id));
        fresh.forEach((b) => seenRef.current.add(b.id));
        return fresh.length || kept.some((b, i) => b !== prev[i]) ? [...kept, ...fresh] : prev;
      });
    } catch (_) { /* transient — next poll */ }
  }, [token, user?.id]);

  useEffect(() => {
    seenRef.current = new Set();
    setQueue([]);
    if (!token || user?.role !== "artisan") return undefined;
    poll();
    const iv = setInterval(poll, POLL_MS);
    const onEvt = (ev) => {
      const t = String(ev?.detail?.type || "");
      if (t === "new_booking" || t.startsWith("booking_")) poll();
    };
    window.addEventListener("yorm:booking-updated", onEvt);
    return () => { clearInterval(iv); window.removeEventListener("yorm:booking-updated", onEvt); };
  }, [token, user?.role, poll]);

  // Watch the request on screen: cancelled / expired while open → explain and move on.
  useEffect(() => {
    if (!bookingId || !token) return undefined;
    setGone(null);
    let stop = false;
    const check = async () => {
      try {
        const { data: b } = await axios.get(`${API}/bookings/${bookingId}?token=${token}`);
        if (stop || !b) return;
        if (["accepted", "declined"].includes(b.status)) { dismiss(); return; }
        const reason = b.status === "cancelled" ? "The customer cancelled this request."
          : b.status === "expired" ? "This request has expired."
          : b.status !== "pending" ? "This request is no longer open."
          : !isLive(b) ? "Time's up — this request has expired." : null;
        if (reason) setGone(reason);
      } catch (e) {
        if (!stop && e?.response?.status === 404) setGone("This request is no longer available.");
      }
    };
    const iv = setInterval(check, 5000);
    const onUpd = (ev) => { if (!ev?.detail?.booking_id || ev.detail.booking_id === bookingId) check(); };
    window.addEventListener("yorm:booking-updated", onUpd);
    return () => { stop = true; clearInterval(iv); window.removeEventListener("yorm:booking-updated", onUpd); };
  }, [bookingId, token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!gone) return undefined;
    const t = setTimeout(() => dismiss(), 4000);
    return () => clearTimeout(t);
  }, [gone]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    setGone(null);
    setQueue((prev) => prev.slice(1).filter(isLive));
  };

  const act = async (status) => {
    if (!booking) return;
    setBusy(true);
    try {
      await axios.put(`${API}/bookings/${booking.id}?token=${token}`, { status });
      toast.success(status === "accepted" ? "Booking accepted!" : "Booking declined");
      notifyBookingUpdated(`booking_${status}`, booking.id);
      dismiss();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${status === "accepted" ? "accept" : "decline"} booking`);
    } finally {
      setBusy(false);
    }
  };

  if (!booking) return null;

  const when = whenLabel(booking);
  const attachments = Array.isArray(booking.attachments) ? booking.attachments : [];
  const images = attachments.filter((u) => /\.(png|jpe?g|webp|gif|heic)(\?|$)/i.test(String(u)));

  return (
    <PersistentSheet open title="New booking request" icon={Briefcase} testId="pro-booking-request-sheet">
      {gone && (
        <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-[15px] font-semibold text-red-700 text-center" data-testid="pro-booking-request-gone">
          {gone}
        </div>
      )}
      <div className="space-y-3">
        <div className="rounded-2xl bg-gradient-to-br from-[#4F988E] to-[#3d7a72] text-white p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-white/90 flex items-center gap-1"><User size={12} /> Customer</p>
            <p className="text-2xl font-bold text-white truncate" data-testid="pro-booking-request-customer">{booking.customer_name || "Customer"}</p>
            {when && (
              <p className="text-xs text-white mt-1 flex items-center gap-1" data-testid="pro-booking-request-when"><Calendar size={12} /> {when}</p>
            )}
          </div>
          <div className="shrink-0 bg-white/95 rounded-xl p-2 shadow-inner">
            <BookingCountdown expiresAt={booking.expires_at} size={72} />
          </div>
        </div>

        {booking.description && (
          <p className="text-[15px] text-gray-800 leading-snug rounded-2xl bg-gray-50 border border-gray-100 px-3 py-2" data-testid="pro-booking-request-description">
            {booking.description}
          </p>
        )}
        {booking.voice_note && (
          <VoiceNotePlayer src={resolveMediaUrl(booking.voice_note, API)} theme="light" testId="pro-booking-request-voice" />
        )}
        {booking.job_address && (
          <div className="flex items-start gap-2 text-sm">
            <MapPin size={16} className="text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Job location</p>
              <p className="text-gray-800" data-testid="pro-booking-request-address">{booking.job_address}</p>
            </div>
          </div>
        )}
        {images.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="pro-booking-request-photos">
            {images.slice(0, 6).map((url, i) => (
              <a key={i} href={resolveMediaUrl(url, API)} target="_blank" rel="noopener noreferrer" className="block">
                <img src={resolveMediaUrl(url, API)} alt={`Attachment ${i + 1}`} loading="lazy" decoding="async"
                  className="w-16 h-16 rounded-md object-cover border border-gray-200" />
              </a>
            ))}
          </div>
        )}
        {attachments.length > images.length && (
          <p className="text-xs text-gray-500 flex items-center gap-1"><Paperclip size={12} /> {attachments.length - images.length} more file{attachments.length - images.length > 1 ? "s" : ""} in the booking</p>
        )}

        {gone ? (
          <Button variant="outline" onClick={dismiss} className="w-full rounded-full" data-testid="pro-booking-request-close">Close</Button>
        ) : (
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button variant="outline" onClick={() => act("declined")} disabled={busy}
              className="rounded-full border-red-200 text-red-600 hover:bg-red-50" data-testid="pro-booking-request-decline">
              <X size={16} className="mr-1" /> Decline
            </Button>
            <Button onClick={() => act("accepted")} disabled={busy}
              className="rounded-full bg-green-600 hover:bg-green-700 text-white" data-testid="pro-booking-request-accept">
              {busy ? <Loader2 size={16} className="animate-spin" /> : (<><Check size={16} className="mr-1" /> Accept job</>)}
            </Button>
            {queue.length > 1 && (
              <p className="col-span-2 text-center text-xs text-gray-500" data-testid="pro-booking-request-queue-count">
                {queue.length - 1} more request{queue.length > 2 ? "s" : ""} waiting
              </p>
            )}
          </div>
        )}
      </div>
    </PersistentSheet>
  );
}
